import * as React from 'react'

import { Render } from '@/components/render-if'
import { Checkbox } from '@/components/ui/checkbox'
import { useFormField } from '@/components/ui/form'
import { cn } from '@/infra/tailwind'

const CheckboxGroupContext = React.createContext<{
  value?: string[]
  onValueChange?: (value: string[]) => void
}>({})

const CheckboxGroup = React.forwardRef<
  HTMLFieldSetElement,
  React.ComponentProps<'fieldset'> & {
    value?: string[]
    onValueChange?: (value: string[]) => void
    defaultValue?: string[]
  }
>(({ className, value, onValueChange, defaultValue, ...props }, ref) => {
  const [selectedValues, setSelectedValues] = React.useState<string[]>(defaultValue ?? [])

  const contextValue = React.useMemo(() => {
    return {
      value: value ?? selectedValues,
      onValueChange: (newValue: string[]) => {
        if (onValueChange) {
          onValueChange(newValue)
        } else {
          setSelectedValues(newValue)
        }
      },
    }
  }, [value, selectedValues, onValueChange])

  return (
    <CheckboxGroupContext.Provider value={contextValue}>
      <fieldset ref={ref} className={cn('flex flex-col gap-2', className)} {...props} />
    </CheckboxGroupContext.Provider>
  )
})
CheckboxGroup.displayName = 'CheckboxGroup'

const CheckboxGroupItem = React.forwardRef<
  React.ComponentRef<typeof Checkbox>,
  React.ComponentPropsWithoutRef<typeof Checkbox> & {
    label?: string
  }
>(({ className, label, value, id, ...props }, ref) => {
  const { value: groupValue, onValueChange } = React.useContext(CheckboxGroupContext)
  const checked = groupValue?.includes(value as string) || false

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (!value || !onValueChange) return

    const newChecked = !checked
    if (newChecked) {
      onValueChange([...(groupValue || []), value as string])
    } else {
      onValueChange((groupValue || []).filter((v) => v !== value))
    }
  }

  return (
    <div data-testid="checkbox-item" className={cn('flex items-center', className)}>
      <div className="flex p-2">
        <Checkbox ref={ref} id={id} checked={checked} onClick={handleClick} type="button" {...props} />
      </div>
      <Render.If condition={label}>
        {(_label) => (
          <label
            htmlFor={id}
            className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {_label}
          </label>
        )}
      </Render.If>
    </div>
  )
})
CheckboxGroupItem.displayName = 'CheckboxGroupItem'

const FormCheckboxGroup = React.forwardRef<
  React.ComponentRef<typeof CheckboxGroup>,
  React.ComponentPropsWithoutRef<typeof CheckboxGroup>
>(({ className, ...props }, ref) => {
  const { error } = useFormField()

  return <CheckboxGroup ref={ref} className={cn(error && 'border-destructive', className)} {...props} />
})
FormCheckboxGroup.displayName = 'FormCheckboxGroup'

export { CheckboxGroup, CheckboxGroupItem, FormCheckboxGroup }
